"use client";

import { useNavigate, useSearch } from "@tanstack/react-router";
import { type PropsWithChildren, useLayoutEffect, useState } from "react";

import {
  HANDOFF_CONVERSATION_PARAM,
  HANDOFF_PANEL_PARAM,
} from "@/react/components/CircleAi/intents/handoff";
import { useCircleAiUrlEntry } from "@/react/components/CircleAi/intents/useCircleAiUrlEntry";
import { useIsCircleAiPageRoute } from "@/react/components/CircleAi/useIsCircleAiPageRoute";

import {
  type CircleAIShellStateProps,
  useCircleAIShellStore,
} from "./store/circleAIShellStore";
import { readStoredDockMode } from "./utils";

type LayoutDefaultState = Partial<
  Omit<CircleAIShellStateProps, "isDockManualOverride">
>;

export interface ProviderProps extends PropsWithChildren {
  defaultState?: LayoutDefaultState;
}

export const Provider = ({
  defaultState,
  children,
}: Readonly<ProviderProps>) => {
  const search = useSearch({ strict: false });
  const { isFullPageAiRoute } = useIsCircleAiPageRoute();
  const navigate = useNavigate();
  useCircleAiUrlEntry();

  // Pure read — no side effects, safe to double-invoke in Strict Mode.
  //
  // Also covers the `?ai=<conversation_id>` deep link: AiConversationPanel
  // (which actually reads/consumes the param) only mounts once
  // conversationPanelState leaves "closed", so nothing reads it on a fresh
  // load unless something already-mounted opens the panel first.
  //
  // Full-page AI routes own the canvas and keep the side panel
  // closed, so all panel handoff params are ignored and stripped there.
  // AiConversationPanel never mounts while closed, so Provider must consume
  // those params itself.
  const [{ shouldOpenPanelOnMount, shouldStripHandoffOnMount }] = useState(
    () => {
      const rawSearch = search as Record<string, unknown>;
      const hasAiParam = !!rawSearch.ai;
      const hasPanelHandoff =
        rawSearch[HANDOFF_PANEL_PARAM] === "open" ||
        !!rawSearch[HANDOFF_CONVERSATION_PARAM];
      return {
        shouldOpenPanelOnMount:
          !isFullPageAiRoute && (hasPanelHandoff || hasAiParam),
        shouldStripHandoffOnMount:
          isFullPageAiRoute && (hasPanelHandoff || hasAiParam),
      };
    },
  );

  useState(() => {
    if (defaultState) {
      const storedDockMode = readStoredDockMode();
      const patch = Object.fromEntries(
        Object.entries(defaultState).filter(
          ([key, value]) =>
            value != null &&
            (key !== "dockMode" || storedDockMode === undefined),
        ),
      );
      if (Object.keys(patch).length > 0) {
        useCircleAIShellStore.setState(patch);
      }
    }
    return null;
  });

  useLayoutEffect(() => {
    if (shouldOpenPanelOnMount) {
      useCircleAIShellStore.setState({ conversationPanelState: "default" });
      void navigate({
        to: ".",
        search: prev => ({ ...prev, [HANDOFF_PANEL_PARAM]: undefined }),
        replace: true,
        resetScroll: false,
      });
    }
    if (shouldStripHandoffOnMount) {
      void navigate({
        to: ".",
        search: prev => ({
          ...prev,
          ai: undefined,
          [HANDOFF_PANEL_PARAM]: undefined,
          [HANDOFF_CONVERSATION_PARAM]: undefined,
        }),
        replace: true,
        resetScroll: false,
      });
    }
  }, [shouldOpenPanelOnMount, shouldStripHandoffOnMount, navigate]);

  return <>{children}</>;
};

Provider.displayName = "CircleAIAppShell.Provider";
